import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";

// Define the company type
interface Company {
  id: string;
  name: string;
  default_language?: string | null;
  settings?: Record<string, any> | null;
  email_sending_enabled?: boolean | null;
  free_trial_days?: number | null;
  payment_grace_days?: number | null;
  skip_subscription_check?: boolean | null;
  total_nbr_cv?: number | null;
  total_nrb_job_offer?: number | null;
}

const companyFormSchema = z.object({
  id: z.string(),
  name: z.string().min(2, { message: "Company name must be at least 2 characters" }),
  default_language: z.string().optional(),
  email_redirect_enabled: z.boolean().default(false),
  email_redirect_to: z.string().email({ message: "Must be a valid email address" }).optional().or(z.literal("")),
  email_sending_enabled: z.boolean().default(false),
  free_trial_days: z.coerce.number().int().min(0).max(365).default(20),
  payment_grace_days: z.coerce.number().int().min(0).max(365).default(10),
  skip_subscription_check: z.boolean().default(false),
  total_nbr_cv: z.union([z.coerce.number().int().min(0), z.literal("")]).nullable().optional(),
  total_nrb_job_offer: z.union([z.coerce.number().int().min(0), z.literal("")]).nullable().optional(),
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

interface EditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyFormValues) => void;
  company: Company;
}

export const EditOrganizationModal = ({ isOpen, onClose, onSubmit, company }: EditOrganizationModalProps) => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useAuth();
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      id: company.id,
      name: company.name,
      default_language: company.default_language || undefined,
      email_redirect_enabled: company.settings?.email_redirect_enabled ?? false,
      email_redirect_to: company.settings?.email_redirect_to ?? "",
      email_sending_enabled: company.email_sending_enabled ?? false,
      free_trial_days: company.free_trial_days ?? 20,
      payment_grace_days: company.payment_grace_days ?? 10,
      skip_subscription_check: company.skip_subscription_check ?? false,
      total_nbr_cv: company.total_nbr_cv ?? null,
      total_nrb_job_offer: company.total_nrb_job_offer ?? null,
    },
  });

  const emailRedirectEnabled = form.watch("email_redirect_enabled");

  const handleSubmit = (values: CompanyFormValues) => {
    onSubmit(values);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[90vw] max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("admin.modals.editCompany.title")}</DialogTitle>
          <DialogDescription>
            {t("admin.company.description")}
          </DialogDescription>
        </DialogHeader>        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("companies.createModal.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Inc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admin.modals.editCompany.defaultLanguage")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("admin.modals.editCompany.selectDefaultLanguage")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="nl">Nederlands</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t("admin.modals.editCompany.defaultLanguageDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email_redirect_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>{t("admin.modals.editCompany.emailRedirectLabel")}</FormLabel>
                    <FormDescription>
                      {t("admin.modals.editCompany.emailRedirectDescription")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {emailRedirectEnabled && (
              <FormField
                control={form.control}
                name="email_redirect_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("admin.modals.editCompany.emailRedirectTo")}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="test@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      {t("admin.modals.editCompany.emailRedirectToDescription")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isSuperAdmin && (
              <>
                <FormField
                  control={form.control}
                  name="email_sending_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel>Email sending enabled</FormLabel>
                        <FormDescription>
                          New organizations cannot send any email until this is turned on. Enable once the organization has been verified (e.g. after contacting support).
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="free_trial_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Free trial days</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} max={365} step={1} {...field} />
                      </FormControl>
                      <FormDescription>
                        Number of free trial days for this organization (default: 20). Changing this value does not change the free trial period ! You must change this value in Stripe directly.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="payment_grace_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment grace period (days)</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} max={365} step={1} {...field} />
                      </FormControl>
                      <FormDescription>
                        How many days to wait after a payment issue is detected before blocking the organization (default: 10).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="skip_subscription_check"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel>Skip subscription verification</FormLabel>
                        <FormDescription>
                          Enable for demo accounts or organizations that do not pay via Stripe. The subscription health check will be ignored entirely.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="total_nbr_cv"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("admin.modals.editCompany.totalNbrCv")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          placeholder={t("admin.modals.editCompany.unlimited")}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                        />
                      </FormControl>
                      <FormDescription>
                        {t("admin.modals.editCompany.totalNbrCvDesc")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="total_nrb_job_offer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("admin.modals.editCompany.totalNrbJobOffer")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          placeholder={t("admin.modals.editCompany.unlimited")}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                        />
                      </FormControl>
                      <FormDescription>
                        {t("admin.modals.editCompany.totalNrbJobOfferDesc")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("common.save")}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
